import { Head, Link, router } from "@inertiajs/react";
import { z } from "zod";
import { useState } from "react";
import { Heading, Box, Flex, Text, Button, Tabs } from "@radix-ui/themes";
import { RefreshCw } from "lucide-react";
import { SpreadsheetPreview } from "@/components/spreadsheet-preview";
import { ColumnAnalyzer } from "@/components/column-analyzer";
import { AnalysisResults } from "@/components/analysis-results";

const TabSchema = z.object({
  id: z.number(),
  name: z.string(),
  preview_data: z.array(z.array(z.string())).nullable(),
  row_count: z.number().nullable(),
});

const CategorySchema = z.object({
  name: z.string(),
  description: z.string(),
  count: z.number(),
});

const AnalyzedColumnSchema = z.object({
  id: z.number(),
  name: z.string(),
  tab_name: z.string(),
  categories: z.array(CategorySchema),
});

const PropsSchema = z.object({
  id: z.number(),
  spreadsheet_id: z.string(),
  file_name: z.string(),
  preview_status: z.enum(["waiting", "loading", "ready", "failed"]),
  analysis_status: z.string(),
  analyzed_columns: z.array(AnalyzedColumnSchema),
  tabs: z.array(TabSchema),
});

type Props = z.infer<typeof PropsSchema>;

export default function SheetsShow(props: Props) {
  const validatedProps = PropsSchema.parse(props);
  const {
    file_name,
    preview_status,
    analysis_status,
    analyzed_columns,
    tabs,
  } = validatedProps;

  const [selectedTab, setSelectedTab] = useState<string>(tabs[0]?.name || "");

  const currentTab = tabs.find((t) => t.name === selectedTab) || tabs[0];
  const headers = currentTab?.preview_data?.[0] || [];

  const handleRefresh = () => {
    router.reload();
  };

  const getStatusMessage = () => {
    switch (preview_status) {
      case "waiting":
        return "Preparing to load spreadsheet preview...";
      case "loading":
        return "Loading spreadsheet preview from Google Sheets...";
      case "failed":
        return "Failed to load spreadsheet preview. Please try refreshing the page.";
      default:
        return null;
    }
  };

  return (
    <Box style={{ minHeight: "100vh" }}>
      <Head title={file_name} />

      <Box mb="6">
        <Link
          href="/"
          style={{
            color: "var(--accent-11)",
            textDecoration: "none",
          }}
          className="hover:opacity-75"
        >
          ← Back to Dashboard
        </Link>
      </Box>

      <AnalysisResults
        analysisStatus={analysis_status}
        analyzedColumns={analyzed_columns}
      />

      {preview_status !== "ready" ? (
        <Flex direction="column" gap="4" align="center" py="8" mt="6">
          <Heading size="6" as="h2">
            {preview_status === "waiting" && "Preparing Preview"}
            {preview_status === "loading" && "Loading Preview"}
            {preview_status === "failed" && "Preview Failed"}
          </Heading>
          <Text style={{ color: "var(--gray-11)" }}>{getStatusMessage()}</Text>
          {preview_status === "waiting" && (
            <>
              <Text size="2" style={{ color: "var(--gray-11)" }}>
                Please try reloading in a few seconds.
              </Text>
              <Button variant="classic" onClick={handleRefresh}>
                <RefreshCw size={16} />
                Reload
              </Button>
            </>
          )}
          {preview_status === "loading" && (
            <Button variant="classic" onClick={handleRefresh}>
              <RefreshCw size={16} />
              Refresh
            </Button>
          )}
          {preview_status === "failed" && (
            <Button variant="classic" onClick={handleRefresh}>
              <RefreshCw size={16} />
              Try Again
            </Button>
          )}
        </Flex>
      ) : tabs.length > 0 ? (
        <>
          <Heading size="5" mb="2">
            Preview
          </Heading>

          <Tabs.Root
            defaultValue={tabs[0]?.name}
            value={selectedTab}
            onValueChange={setSelectedTab}
          >
            <Tabs.List>
              {tabs.map((tab) => (
                <Tabs.Trigger key={tab.id} value={tab.name}>
                  {tab.name}
                  {tab.row_count !== null && (
                    <Text
                      as="span"
                      size="2"
                      style={{
                        color: "var(--gray-11)",
                        marginLeft: "var(--space-2)",
                      }}
                    >
                      ({tab.row_count.toLocaleString()})
                    </Text>
                  )}
                </Tabs.Trigger>
              ))}
            </Tabs.List>

            <Box pt="4">
              {tabs.map((tab) => (
                <Tabs.Content key={tab.id} value={tab.name}>
                  {tab.preview_data && tab.preview_data.length > 0 ? (
                    <SpreadsheetPreview previewData={tab.preview_data} />
                  ) : (
                    <Text style={{ color: "var(--gray-11)" }}>
                      No data available for this tab.
                    </Text>
                  )}
                </Tabs.Content>
              ))}
            </Box>
          </Tabs.Root>

          <ColumnAnalyzer
            sheetId={validatedProps.id}
            tabId={currentTab.id}
            columns={headers}
          />
        </>
      ) : (
        <Text style={{ color: "var(--gray-11)" }}>
          No tabs found in this spreadsheet.
        </Text>
      )}
    </Box>
  );
}
